/** How much surrounding text a match carries as its label. */
const SNIPPET_CONTEXT_CHARS = 48

export interface TextMatch {
  /** Character offset of the match in the source text. */
  index: number
  /** 1-based line the match starts on. */
  line: number
  /** Surrounding text, trimmed to one line's worth of context. */
  snippet: string
}

/**
 * Every case-insensitive occurrence of `query` in `text`, with its line number.
 *
 * Used by the plain-text card previews (FS-657) so a search can list all of its
 * matches instead of stepping through them.
 */
export function findTextMatches(text: string, query: string): TextMatch[] {
  const term = query.trim().toLowerCase()
  if (!text || !term) return []

  const haystack = text.toLowerCase()
  const matches: TextMatch[] = []
  let from = 0
  let line = 1
  let scanned = 0

  for (;;) {
    const index = haystack.indexOf(term, from)
    if (index === -1) break

    // Count newlines incrementally rather than re-splitting the text per match.
    for (let i = scanned; i < index; i++) {
      if (text[i] === '\n') line++
    }
    scanned = index

    const start = Math.max(0, index - SNIPPET_CONTEXT_CHARS)
    const end = Math.min(text.length, index + term.length + SNIPPET_CONTEXT_CHARS)
    let snippet = text.slice(start, end).replace(/\s+/g, ' ').trim()
    if (start > 0) snippet = `...${snippet}`
    if (end < text.length) snippet = `${snippet}...`

    matches.push({ index, line, snippet })
    // Non-overlapping, like browser find-in-page: "aa" in "aaa" is one hit, not
    // two. Counting overlaps also broke the caller, which renders one <mark> per
    // match — the second, overlapping mark can't be drawn over the same
    // characters, so it silently had no active highlight to scroll to.
    from = index + term.length
  }

  return matches
}
